/**
 * **Every status colour in the staff app, in one map** (C14 §10.6 item 4,
 * ADR-0033 §2; T-6.13).
 *
 *     const { tone, label } = statusTone('invoice', row.payment_state)
 *     <Badge tone={tone}>{label}</Badge>        // or <StatusBadge kind=… status=… />
 *
 * A page never writes a palette colour and never picks a tone for a status on
 * its own — two screens that disagree about whether *part paid* is good news
 * is what this file ends. The keys are the service's own vocabularies, read
 * from `services/prodesk-service/app/models` (named beside each kind); the
 * labels are the ones the screens already used, imported where a screen
 * already exported them so there is still one copy of each word.
 *
 * The tone rule, so a new status lands consistently:
 * - `success` — finished well, nothing left to do (paid, won, imported);
 * - `warning` — waiting on the firm, or needs a look (unpaid, in review);
 * - `error` — late, refused or broken (overdue, failed, rejected);
 * - `info` — moving along normally (issued, in progress, sent);
 * - `neutral` — not started, or closed with nothing to act on (draft,
 *   cancelled, ended, skipped).
 *
 * An unknown status is never an error: it is `neutral` with its own words
 * humanised (`proposal_sent` → *Proposal sent*), because a case's status is a
 * firm's own stage name and a new service vocabulary must not blank a badge.
 */

import type { BadgeTone } from '@fiveox/ui'

import { PAYMENT_LABELS } from '../../lib/api/billing'
import { stateLabel } from '../../lib/api/calendar'
import { CATEGORY_LABELS } from '../../lib/api/cases'
import { STAGE_LABELS } from '../../lib/api/crm'
import { STATUS_LABELS as PROPOSAL_LABELS } from '../../lib/api/proposals'

export type StatusKind =
  | 'case'
  | 'task'
  | 'invoice'
  | 'receipt'
  | 'retainer'
  | 'lead'
  | 'proposal'
  | 'import'
  | 'calendar'
  | 'request'
  | 'booking'
  | 'flow'
  | 'catalogue'

export interface StatusLook {
  tone: BadgeTone
  label: string
}

type Map = Record<string, StatusLook>

const look = (tone: BadgeTone, label: string): StatusLook => ({ tone, label })

export const STATUS_MAP: Record<StatusKind, Map> = {
  // A case's `category` (models/services.py STAGE_CATEGORIES). Its `status` is
  // the firm's own stage name — pass the category and, if you like, the stage
  // name as the label (`StatusBadge label=`).
  case: {
    open: look('neutral', CATEGORY_LABELS.open),
    in_progress: look('info', CATEGORY_LABELS.in_progress),
    review: look('warning', CATEGORY_LABELS.review),
    completed: look('success', CATEGORY_LABELS.completed),
    cancelled: look('neutral', CATEGORY_LABELS.cancelled),
  },
  // A task has `is_done` and a `due_date`, not a status column
  // (models/cases.py Task); these are the four a screen derives from them —
  // `taskStatus()` below does it once.
  task: {
    open: look('neutral', 'To do'),
    due_today: look('warning', 'Due today'),
    overdue: look('error', 'Overdue'),
    done: look('success', 'Done'),
  },
  // models/billing.py INVOICE_STATUSES plus the server's `payment_state`
  // (lib/api/billing.ts PaymentState), and `overdue` for a screen that marks
  // an unpaid invoice past its due date (FR-L5).
  invoice: {
    draft: look('neutral', PAYMENT_LABELS.draft),
    issued: look('info', PAYMENT_LABELS.issued),
    unpaid: look('warning', PAYMENT_LABELS.unpaid),
    part_paid: look('info', PAYMENT_LABELS.part_paid),
    paid: look('success', PAYMENT_LABELS.paid),
    overdue: look('error', 'Overdue'),
    cancelled: look('neutral', PAYMENT_LABELS.cancelled),
  },
  // A receipt has no status column. These are the states its fields say
  // (lib/api/billing.ts `receiptFlags`, ADR-0027 §4–§5): wholly applied, some
  // of it held as an advance, and the gateway's refund and dispute facts
  // (models/billing.py GATEWAY_REFUND_STATUSES).
  receipt: {
    applied: look('success', 'Applied'),
    advance: look('info', 'Advance held'),
    overpaid: look('warning', 'Overpaid'),
    disputed: look('warning', 'Disputed'),
    refund_requested: look('info', 'Refund requested'),
    refund_settled: look('neutral', 'Refunded'),
    refund_refused: look('error', 'Refund refused'),
  },
  // models/billing.py RETAINER_STATUSES.
  retainer: {
    active: look('success', 'Active'),
    paused: look('warning', 'Paused'),
    ended: look('neutral', 'Ended'),
  },
  // models/crm.py LEAD_STAGES, and LEAD_STATUSES' `merged`.
  lead: {
    new: look('info', STAGE_LABELS.new),
    contacted: look('info', STAGE_LABELS.contacted),
    qualified: look('warning', STAGE_LABELS.qualified),
    proposal_sent: look('warning', STAGE_LABELS.proposal_sent),
    won: look('success', STAGE_LABELS.won),
    lost: look('neutral', STAGE_LABELS.lost),
    active: look('info', 'Active'),
    merged: look('neutral', 'Merged'),
  },
  // models/crm.py PROPOSAL_STATUSES.
  proposal: {
    draft: look('neutral', PROPOSAL_LABELS.draft),
    sent: look('info', PROPOSAL_LABELS.sent),
    accepted: look('success', PROPOSAL_LABELS.accepted),
    declined: look('error', PROPOSAL_LABELS.declined),
    expired: look('neutral', PROPOSAL_LABELS.expired),
  },
  // models/imports.py JOB_STATUSES, then ROW_STATUSES (no key is in both).
  // A committed job with refused rows is a warning — `importJobTone` in
  // routes/Imports says so from the job, which a status alone cannot.
  import: {
    queued: look('info', 'Waiting'),
    running: look('info', 'Checking'),
    previewed: look('info', 'Checked — not imported yet'),
    committed: look('success', 'Imported'),
    failed: look('error', 'Could not be read'),
    ready: look('info', 'Would import'),
    created: look('success', 'Imported'),
    skipped: look('warning', 'Already here'),
    rejected: look('error', 'Refused'),
  },
  // lib/api/calendar.ts OccurrenceState (the screen's word for a period), then
  // models/calendar.py OCCURRENCE_STATUSES and RUN_STATUSES.
  calendar: {
    opened: look('success', stateLabel('opened')),
    awaiting_case: look('warning', stateLabel('awaiting_case')),
    overdue: look('error', stateLabel('overdue')),
    skipped: look('neutral', stateLabel('skipped')),
    pending: look('warning', 'Pending'),
    failed: look('error', 'Failed'),
    running: look('info', 'Running'),
    completed: look('success', 'Completed'),
  },
  // A service request (models/cases.py REQUEST_STATUSES), then a document
  // request (models/documents.py REQUEST_STATES) — no key is in both.
  request: {
    new: look('warning', 'New'),
    accepted: look('success', 'Accepted'),
    declined: look('neutral', 'Declined'),
    pending: look('warning', 'Waiting on the client'),
    received: look('info', 'Received'),
    rejected: look('error', 'Sent back'),
    verified: look('success', 'Verified'),
  },
  // w19b — models/booking.py BOOKING_STATUSES (C19, ADR-0037 §4). A request
  // waits on the firm to put a price on it; one awaiting payment has an issued
  // invoice behind it.
  booking: {
    started: look('info', 'In progress'),
    awaiting_payment: look('warning', 'Awaiting payment'),
    // w27a — C25, ADR-0052 §2, §7: a bank-QR proof waits for the firm.
    payment_review: look('warning', 'Proof to check'),
    confirmed: look('success', 'Confirmed'),
    request: look('warning', 'Request'),
    cancelled: look('neutral', 'Cancelled'),
    expired: look('neutral', 'Expired'),
  },
  // w19b — a booking flow's status (models/booking.py FLOW_STATUSES).
  flow: {
    draft: look('neutral', 'Draft'),
    live: look('success', 'Live'),
    paused: look('warning', 'Paused'),
  },
  // w27a — a category, a service or a plan (models/services.py LIFECYCLE;
  // C25, ADR-0054 §1). Inactive is paused — the firm's to switch back on.
  catalogue: {
    active: look('success', 'Active'),
    inactive: look('warning', 'Inactive'),
    deleted: look('neutral', 'Deleted'),
  },
}

/** `proposal_sent` → *Proposal sent*; `IN-REVIEW` → *In review*. */
export function humanise(status: string): string {
  const words = status.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLowerCase()
  return words ? words[0]!.toUpperCase() + words.slice(1) : '—'
}

/** The tone and the words for one status of one kind. Unknown → neutral. */
export function statusTone(kind: StatusKind, status: string | null | undefined): StatusLook {
  if (!status) return { tone: 'neutral', label: '—' }
  return STATUS_MAP[kind][status] ?? { tone: 'neutral', label: humanise(status) }
}

/** A task's status from its two fields — `today` is `YYYY-MM-DD`, the firm's
 *  own day, passed in so a test is not at the mercy of the clock. */
export function taskStatus(
  task: { is_done: boolean; due_date: string | null },
  today: string,
): 'done' | 'overdue' | 'due_today' | 'open' {
  if (task.is_done) return 'done'
  if (task.due_date && task.due_date < today) return 'overdue'
  if (task.due_date === today) return 'due_today'
  return 'open'
}
